import React from 'react';
import { AlertTriangle, Trash2, Loader2, X } from 'lucide-react';
import { Button } from '../ui/Button';

interface ConfirmDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title?: string;
  itemName?: string;
  itemType?: 'bộ câu hỏi' | 'câu hỏi';
  loading?: boolean;
}

export const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  itemName,
  itemType = 'bộ câu hỏi',
  loading = false
}) => {
  if (!isOpen) return null;

  const defaultTitle = `Xóa ${itemType}`;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div 
        className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200 relative transform transition-all animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          type="button"
          disabled={loading}
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-1.5 rounded-xl hover:bg-slate-100 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center mb-4 ring-8 ring-rose-50">
            <AlertTriangle className="w-7 h-7" />
          </div>

          <h3 className="text-lg font-bold text-slate-900 mb-2">
            {title || defaultTitle}
          </h3>

          <p className="text-sm font-semibold text-slate-700 mb-1">
            Bạn có chắc chắn muốn xóa {itemType} này không?
          </p>

          {itemName && (
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl w-full my-3 text-xs font-bold text-slate-800 line-clamp-2">
              "{itemName}"
            </div>
          )}

          <p className="text-xs text-rose-600 font-medium mb-6 leading-relaxed">
            {itemType === 'bộ câu hỏi' 
              ? 'Lưu ý: Tất cả các câu hỏi và dữ liệu liên quan bên trong bộ câu hỏi sẽ bị xóa vĩnh viễn và không thể khôi phục.' 
              : 'Lưu ý: Thao tác này sẽ xóa vĩnh viễn câu hỏi này khỏi bộ đề và không thể khôi phục.'}
          </p>

          <div className="flex items-center gap-3 w-full justify-center">
            <Button
              type="button"
              variant="secondary"
              disabled={loading}
              onClick={onClose}
              className="flex-1 text-xs sm:text-sm font-bold"
            >
              Hủy bỏ
            </Button>
            <Button
              type="button"
              disabled={loading}
              onClick={onConfirm}
              className="flex-1 bg-rose-600 hover:bg-rose-700 text-white shadow-md shadow-rose-200 text-xs sm:text-sm font-bold flex items-center justify-center space-x-1.5"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Đang xóa...</span>
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  <span>Xóa vĩnh viễn</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
